import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ShippingArticle() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <article className="pt-24 pb-16">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-500 mb-6 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              {t('back_to_home')}
            </button>

            <div className="mb-8">
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-800 text-xs font-medium rounded-full mb-4">
                {t('article_category_shipping')}
              </span>
              <h1 className="text-3xl md:text-5xl font-heading font-semibold text-foreground-950 mb-4 leading-tight">
                CSSBuy Shipping Cost Guide 2026 — Methods, Prices & Weight Explained
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span>{t('published_date')}: 2026-08-20</span>
                <span>{t('updated_date')}: 2026-08-27</span>
                <span>6 min read</span>
              </div>
            </div>

            <div className="aspect-[21/9] rounded-xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Minimalist%20shipping%20packages%20stacked%20on%20soft%20neutral%20linen%20surface%20with%20shipping%20labels%20warm%20natural%20light%20editorial%20photography%20clean%20aesthetic%20logistics%20delivery%20concept%20premium%20quality&width=1200&height=500&seq=article-hero-shipping&orientation=landscape"
                alt="CSSBuy Shipping Guide"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed space-y-8">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                  Understanding CSSBuy Shipping Costs
                </h2>
                <p>
                  Shipping is one of the most important factors to consider when using CSSBuy as your purchasing agent. Understanding how shipping costs are calculated can help you save money and avoid surprises when it is time to send your items home. This guide covers everything you need to know about CSSBuy shipping in 2026, including methods, pricing, weight calculations, and tips for reducing costs.
                </p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                  Shipping Methods Available
                </h2>
                <p>
                  CSSBuy offers multiple shipping lines to cater to different needs and budgets. The most common options include EMS (Express Mail Service), DHL, FedEx, UPS, and various postal services. Each method has its own advantages in terms of speed, cost, tracking reliability, and customs handling.
                </p>
                <div className="overflow-x-auto my-4">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-background-100">
                        <th className="text-left p-3 font-semibold border border-background-200">Method</th>
                        <th className="text-left p-3 font-semibold border border-background-200">Speed</th>
                        <th className="text-left p-3 font-semibold border border-background-200">Cost</th>
                        <th className="text-left p-3 font-semibold border border-background-200">Best For</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="p-3 border border-background-200">EMS</td>
                        <td className="p-3 border border-background-200">7-15 days</td>
                        <td className="p-3 border border-background-200">Medium</td>
                        <td className="p-3 border border-background-200">Balanced cost/speed</td>
                      </tr>
                      <tr className="bg-background-50">
                        <td className="p-3 border border-background-200">DHL</td>
                        <td className="p-3 border border-background-200">3-7 days</td>
                        <td className="p-3 border border-background-200">Higher</td>
                        <td className="p-3 border border-background-200">Fast delivery</td>
                      </tr>
                      <tr>
                        <td className="p-3 border border-background-200">FedEx</td>
                        <td className="p-3 border border-background-200">3-7 days</td>
                        <td className="p-3 border border-background-200">Higher</td>
                        <td className="p-3 border border-background-200">Fast delivery</td>
                      </tr>
                      <tr className="bg-background-50">
                        <td className="p-3 border border-background-200">Postal (Small)</td>
                        <td className="p-3 border border-background-200">14-30 days</td>
                        <td className="p-3 border border-background-200">Lower</td>
                        <td className="p-3 border border-background-200">Small/light items</td>
                      </tr>
                      <tr>
                        <td className="p-3 border border-background-200">Sea Mail</td>
                        <td className="p-3 border border-background-200">30-60 days</td>
                        <td className="p-3 border border-background-200">Lowest</td>
                        <td className="p-3 border border-background-200">Heavy/large items</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                  How Weight Affects Shipping Cost
                </h2>
                <p>
                  CSSBuy shipping costs are primarily based on weight, but there is an important distinction between actual weight and volumetric weight. Actual weight is the physical weight of the package, while volumetric weight is calculated based on the package dimensions. Shipping carriers charge based on whichever is higher.
                </p>
                <p>
                  Volumetric weight is typically calculated using the formula: (Length × Width × Height) / Volumetric Divisor. The divisor varies by shipping method, usually ranging from 5000 to 6000. This means that large but lightweight items, like shoe boxes or clothing boxes, can be charged based on their volume rather than their actual weight.
                </p>
                <p>
                  To minimize shipping costs, consider removing unnecessary packaging before shipping. Shoe boxes, bulky clothing tags, and excess packaging materials can significantly increase the volumetric weight of your package. CSSBuy offers a rehearsal packaging service that helps you estimate the final weight before choosing a shipping method.
                </p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                  Shipping to Brazil
                </h2>
                <p>
                  For Brazilian buyers, CSSBuy offers shipping through EMS and postal services. EMS is generally the most reliable option for Brazil, with reasonable delivery times and tracking capabilities. However, be aware that Brazilian customs are strict, and packages may be subject to import duties and taxes.
                </p>
                <p>
                  The Brazilian import tax threshold is relatively low, and most packages over a certain value will be taxed. Duties typically range from 60% of the product value plus ICMS (state tax), which varies by state. It is important to factor these costs into your total budget when ordering.
                </p>
                <p>
                  To reduce the risk of customs issues, keep the declared value realistic and accurate. CSSBuy allows you to adjust the declared value within reasonable limits, but extremely low declarations can raise suspicions and lead to additional inspections or seizures.
                </p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                  Tips to Save on Shipping
                </h2>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Consolidate multiple items into one package to reduce per-item shipping costs.</li>
                  <li>Remove unnecessary packaging like shoe boxes and heavy tags.</li>
                  <li>Choose slower shipping methods for non-urgent orders.</li>
                  <li>Consider rehearsal packaging to get an accurate weight estimate.</li>
                  <li>Split large orders into smaller packages to avoid high-value customs declarations.</li>
                  <li>Compare shipping methods for your specific destination before finalizing.</li>
                </ul>
              </section>

              <section className="bg-background-100 rounded-xl p-6 md:p-8">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-4">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium text-foreground-900 mb-1">How long does shipping take?</h3>
                    <p className="text-sm">Shipping times vary by method: 3-7 days for express, 7-15 days for standard, and 14-60 days for economy options.</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 mb-1">What is volumetric weight?</h3>
                    <p className="text-sm">Volumetric weight is calculated from package dimensions. Carriers charge the higher of actual weight or volumetric weight.</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 mb-1">Can I track my package?</h3>
                    <p className="text-sm">Yes, all shipping methods provide tracking numbers. You can track through CSSBuy or directly with the carrier.</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 mb-1">What happens if my package is lost?</h3>
                    <p className="text-sm">CSSBuy offers insurance options for some shipping methods. Check the insurance policy before shipping.</p>
                  </div>
                </div>
              </section>
            </div>

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="bg-primary-50 rounded-xl p-6 md:p-8 text-center">
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">
                  Ready to ship your items?
                </h3>
                <p className="text-sm text-foreground-600 mb-6">
                  Browse products on the CSSBuy Spreadsheet and start your buying journey today.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors text-sm whitespace-nowrap"
                  >
                    <i className="ri-external-link-line" />
                    {t('explore_spreadsheet')}
                  </a>
                  <button
                    onClick={() => navigate('/cssbuy-safety-guide')}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-foreground-700 font-medium rounded-lg border border-background-200 hover:bg-background-100 transition-colors text-sm whitespace-nowrap"
                  >
                    <i className="ri-arrow-right-line" />
                    {t('view_safety_guide')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}